import React, { useState } from 'react';
import Button from '../common/Button';
import StatusMessage from '../common/StatusMessage';
import Loading from '../common/Loading';
import { generateReport, generateROITable, downloadFile } from '../../services/reportService';
import { Chart } from 'chart.js/auto';

// Helper function to convert base64 to Blob
function base64ToBlob(base64, mimeType) {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
}

// Generate chart in browser and return base64
function generateChartBase64(processes) {
  console.log('[generateChartBase64] Starting with', processes.length, 'processes');

  return new Promise((resolve, reject) => {
    try {
      // Prepare data
      const complexityScores = [];
      const weightedScores = [];
      const processIds = [];

      processes.forEach(proc => {
        const complexityScore = parseFloat(proc.complexity_score || 3);
        const weightedScore = parseFloat(proc.weighted_score || 0);
        complexityScores.push(complexityScore);
        weightedScores.push(weightedScore);
        processIds.push(proc.id || '');
      });

      console.log('[generateChartBase64] Data prepared:', {
        points: complexityScores.length,
        sample: { complexity: complexityScores[0], score: weightedScores[0], id: processIds[0] }
      });

    // Calculate averages
    const avgComplexity = complexityScores.length > 0
      ? complexityScores.reduce((a, b) => a + b, 0) / complexityScores.length
      : 3;
    const avgScore = weightedScores.length > 0
      ? weightedScores.reduce((a, b) => a + b, 0) / weightedScores.length
      : 1.5;

    // Create canvas
    const canvas = document.createElement('canvas');
    canvas.width = 800;
    canvas.height = 600;
    const ctx = canvas.getContext('2d');

    console.log('[generateChartBase64] Canvas created, creating Chart.js instance');

    // Create chart
    new Chart(ctx, {
      type: 'scatter',
      data: {
        datasets: [{
          label: 'Processes',
          data: complexityScores.map((x, i) => ({ x, y: weightedScores[i] })),
          backgroundColor: 'rgba(76, 175, 80, 0.6)',
          borderColor: 'rgba(0, 0, 0, 1)',
          borderWidth: 1,
          pointRadius: 8
        }]
      },
      options: {
        responsive: false,
        animation: false,  // Disable animation for faster rendering
        plugins: {
          title: {
            display: true,
            text: 'Prioritization Matrix',
            font: { size: 18, weight: 'bold' }
          },
          legend: { display: false }
        },
        scales: {
          x: {
            title: { display: true, text: 'Complexity', font: { size: 14, weight: 'bold' } },
            min: 0.5,
            max: 5.5,
            ticks: {
              stepSize: 1,
              callback: (value) => {
                const labels = { 1: 'Very Low\n(1)', 2: 'Low\n(2)', 3: 'Medium\n(3)', 4: 'High\n(4)', 5: 'Custom\n(5)' };
                return labels[value] || value;
              }
            }
          },
          y: {
            title: { display: true, text: 'Impact to Contact Center', font: { size: 14, weight: 'bold' } }
          }
        }
      },
      plugins: [{
        id: 'labels',
        afterDatasetsDraw: (chart) => {
          const ctx = chart.ctx;
          chart.data.datasets[0].data.forEach((point, index) => {
            const meta = chart.getDatasetMeta(0).data[index];
            ctx.fillStyle = 'black';
            ctx.font = '10px Arial';
            ctx.fillText(processIds[index], meta.x + 5, meta.y - 5);
          });
        }
      }]
    });

      // Wait for chart to render, then convert to base64
      setTimeout(() => {
        try {
          console.log('[generateChartBase64] Converting canvas to base64...');
          const dataUrl = canvas.toDataURL('image/png');

          if (!dataUrl || !dataUrl.includes('data:image/png')) {
            console.error('[generateChartBase64] Invalid data URL:', dataUrl?.substring(0, 50));
            reject(new Error('Failed to convert canvas to data URL'));
            return;
          }

          const base64 = dataUrl.split(',')[1];

          if (!base64 || base64.length < 100) {
            console.error('[generateChartBase64] Invalid base64 length:', base64?.length);
            reject(new Error('Invalid base64 data from canvas'));
            return;
          }

          console.log('[generateChartBase64] Success! Base64 length:', base64.length);
          resolve(base64);
        } catch (err) {
          console.error('[generateChartBase64] Error in setTimeout:', err);
          reject(err);
        }
      }, 100);
    } catch (err) {
      console.error('[generateChartBase64] Error in try block:', err);
      reject(err);
    }
  });
}

function ReportDownloadPanel({ customerId, contextData, processes }) {
  const [loading, setLoading] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [status, setStatus] = useState(null);
  const [reportUrls, setReportUrls] = useState(null);

  const handleGenerateReport = async () => {
    if (!customerId || !contextData) {
      setStatus({ type: 'error', message: 'Please save context first' });
      return;
    }

    // Get processes from props or localStorage
    let processesToUse = processes;
    if (!processesToUse || processesToUse.length === 0) {
      const savedProcesses = localStorage.getItem(`processes_${customerId}`);
      if (savedProcesses) {
        processesToUse = JSON.parse(savedProcesses);
        console.log('Loaded processes from localStorage:', processesToUse.length);
      }
    }

    if (!processesToUse || processesToUse.length === 0) {
      setStatus({ type: 'error', message: 'Please save the process list first (click "Save Process List" button)' });
      return;
    }

    setLoading(true);
    setStatus(null);
    setReportUrls(null);

    try {
      console.log('Starting chart generation with', processesToUse.length, 'processes');

      // Generate chart in browser
      const chartBase64 = await generateChartBase64(processesToUse);
      console.log('Chart generated successfully! Base64 length:', chartBase64?.length);

      if (!chartBase64) {
        throw new Error('Failed to generate chart - no data returned');
      }

      console.log('Sending request to backend with chart');

      // Send chart + data to backend
      const result = await generateReport(customerId, contextData, processesToUse, chartBase64);

      console.log('Report generated successfully!');

      // Convert base64 to blob URLs
      const reportBlob = base64ToBlob(result.reportBase64, 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');
      const chartBlob = base64ToBlob(result.chartBase64, 'image/png');

      const reportUrl = URL.createObjectURL(reportBlob);
      const chartUrl = URL.createObjectURL(chartBlob);

      setReportUrls({
        report: reportUrl,
        chart: chartUrl
      });

      setStatus({
        type: 'success',
        message: 'Report generated successfully! Click buttons below to download.'
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: `Failed to generate report: ${error.response?.data?.error || error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateROITable = async () => {
    if (!customerId || !contextData) {
      setStatus({ type: 'error', message: 'Please save context first' });
      return;
    }

    // Get processes from props or localStorage
    let processesToUse = processes;
    if (!processesToUse || processesToUse.length === 0) {
      const savedProcesses = localStorage.getItem(`processes_${customerId}`);
      if (savedProcesses) {
        processesToUse = JSON.parse(savedProcesses);
      }
    }

    if (!processesToUse || processesToUse.length === 0) {
      setStatus({ type: 'error', message: 'No processes to generate table from' });
      return;
    }

    setLoadingTable(true);
    setStatus(null);

    try {
      console.log('Generating standalone ROI table with merged cells...');

      const result = await generateROITable(customerId, contextData, processesToUse);

      console.log('ROI table generated successfully!');

      // Convert base64 to blob
      const byteCharacters = atob(result.tableBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const tableBlob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });

      const tableUrl = URL.createObjectURL(tableBlob);
      downloadFile(tableUrl, 'ROI_Analysis_Table.docx');

      setStatus({
        type: 'success',
        message: '✅ ROI table with merged cells downloaded successfully!'
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: `Failed to generate ROI table: ${error.response?.data?.error || error.message}`
      });
    } finally {
      setLoadingTable(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md mb-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Generate Report</h2>
      <p className="text-gray-600 text-sm mb-6">Generate a professional Word document with all your context, processes, and a complexity-impact chart. Make sure to save your process list first.</p>

      {status && (
        <StatusMessage
          type={status.type}
          message={status.message}
          onClose={() => setStatus(null)}
        />
      )}

      {loading && <Loading message="Generating Word document and chart..." />}

      {!loading && !loadingTable && (
        <>
          <div className="mb-4">
            <Button
              variant="success"
              onClick={handleGenerateReport}
              disabled={!customerId}
              className="w-full"
            >
              Generate Full Report
            </Button>
          </div>

          {reportUrls && (
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm font-semibold text-gray-700 mb-3">Download the following files:</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button
                  variant="primary"
                  onClick={() => downloadFile(reportUrls.report, 'IPA_Report.docx')}
                >
                  IPA Report (Word Document)
                </Button>

                <Button
                  variant="secondary"
                  onClick={() => downloadFile(reportUrls.chart, 'complexity_impact_chart.png')}
                >
                  Process Chart (Image)
                </Button>

                <Button
                  variant="primary"
                  onClick={handleGenerateROITable}
                  disabled={loadingTable}
                >
                  {loadingTable ? 'Generating...' : 'ROI Table (Word Document)'}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {loadingTable && <Loading message="Generating ROI table with merged cells..." />}

      <p className="mt-4 text-sm text-gray-600">
        Note: Make sure you have saved the final process list (from the Process Assessment section) before generating the report.
      </p>
    </div>
  );
}

export default ReportDownloadPanel;
